'use client';

/* Representment Engine — agent flow diagram + LIVE animated console demo. */
import { useEffect, useState } from 'react';
import { Icon } from '../icon';
import { IconTile, SectionHead, rd, toneVar, useReveal } from '../primitives';
import { agents, queue } from '../data';
import type { Agent, QueueItem, Tone } from '../data';

interface MiniRingProps {
  value: number;
  threshold: number;
  size?: number;
  stroke?: number;
}

/* small confidence ring */
function MiniRing({ value, threshold, size = 38, stroke = 4 }: MiniRingProps) {
  const r = (size - stroke) / 2,
    c = 2 * Math.PI * r;
  const met = value >= threshold;
  const tone = met ? 'var(--green)' : 'var(--amber)';
  return (
    <div style={{ width: size, height: size, position: 'relative', flexShrink: 0 }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={tone}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${value * c} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray .7s' }}
        />
      </svg>
      <span className="tnum" style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 600, color: tone }}>{Math.round(value * 100)}</span>
    </div>
  );
}

/* the live, looping queue */
function LiveConsole() {
  const q = queue;
  const total = q.length;
  const [shown, setShown] = useState<number>(0);
  const [resolved, setResolved] = useState<number>(0);
  const reduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reduce) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShown(total);
      setResolved(total);
      return;
    }
    let t: ReturnType<typeof setTimeout> | undefined;
    if (shown < total && shown === resolved) {
      t = setTimeout(() => setShown((s: number) => s + 1), 1250);
    } else if (shown > resolved) {
      t = setTimeout(() => setResolved((r: number) => r + 1), 1000);
    } else if (shown === total && resolved === total) {
      t = setTimeout(() => {
        setShown(0);
        setResolved(0);
      }, 3000);
    }
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, resolved]);

  const autoCount = q.slice(0, resolved).filter((x: QueueItem) => x.status === 'auto').length;
  const hitlCount = resolved - autoCount;

  return (
    <div className="card console" style={{ overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>
      <div className="row" style={{ gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--line-soft)', background: 'var(--bg-sunken)' }}>
        <span style={{ width: 11, height: 11, borderRadius: 50, background: 'var(--rose)' }} />
        <span style={{ width: 11, height: 11, borderRadius: 50, background: 'var(--amber)' }} />
        <span style={{ width: 11, height: 11, borderRadius: 50, background: 'var(--green)' }} />
        <span className="mono subtle" style={{ fontSize: 12, marginLeft: 8 }}>FRAUDefender · Unified queue</span>
        <span className="pill" style={{ marginLeft: 'auto', height: 24, fontSize: 11.5 }}><span className="pill-dot" style={{ animation: 'pulse-dot 1.8s infinite' }} />live</span>
      </div>
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 326 }}>
        {q.map((row: QueueItem, idx: number) => {
          if (idx >= shown) return null;
          const isResolved = idx < resolved;
          const c = toneVar(row.tone);
          const auto = row.status === 'auto';
          return (
            <div key={row.ref} className="console-row" style={{ border: '1px solid var(--line-soft)', borderRadius: 12, padding: '12px 14px', background: 'var(--bg-1)',
              animation: 'row-in .45s cubic-bezier(.2,.7,.3,1)' }}>
              <div className="between" style={{ gap: 12 }}>
                <div className="row" style={{ gap: 11, minWidth: 0 }}>
                  <span style={{ width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', color: c, background: `color-mix(in oklch, ${c} 16%, var(--bg-1))`, border: `1px solid color-mix(in oklch, ${c} 32%, transparent)`, flexShrink: 0 }}>
                    <Icon name="bot" size={16} />
                  </span>
                  <div className="stack" style={{ gap: 1, minWidth: 0 }}>
                    <div className="row" style={{ gap: 7 }}>
                      <span className="mono" style={{ fontSize: 12, color: 'var(--fg)' }}>{row.ref}</span>
                      <span className="tag" style={{ height: 17, fontSize: 9.5, padding: '0 5px' }}>{row.type}</span>
                    </div>
                    <span className="subtle" style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.merchant} · {row.amount}</span>
                  </div>
                </div>
                <div className="row" style={{ gap: 12, flexShrink: 0 }}>
                  <MiniRing value={isResolved ? row.conf : 0.04} threshold={row.threshold} />
                  {!isResolved ? (
                    <span className="row mono" style={{ gap: 7, fontSize: 11.5, color: c, width: 122, justifyContent: 'flex-end' }}>
                      <Icon name="refresh-cw" size={13} style={{ animation: 'spin 1s linear infinite' }} />{row.agent.split(' ')[0]}…
                    </span>
                  ) : (
                    <span className="row" style={{ gap: 6, fontSize: 12, fontWeight: 500, width: 122, justifyContent: 'flex-end',
                      color: auto ? 'var(--green)' : 'var(--amber)' }}>
                      <Icon name={auto ? 'circle-check' : 'arrow-up-right'} size={15} />{auto ? row.action : 'Needs human'}
                    </span>
                  )}
                </div>
              </div>
              {isResolved && (
                <div className="row" style={{ gap: 7, marginTop: 9, paddingTop: 9, borderTop: '1px dashed var(--line-soft)' }}>
                  <span className="tag" style={{ height: 18, fontSize: 9.5, color: c, borderColor: `color-mix(in oklch, ${c} 36%, transparent)` }}>{row.agent}</span>
                  <span className="subtle pretty" style={{ fontSize: 11.5, lineHeight: 1.4 }}>{row.reason}</span>
                </div>
              )}
            </div>
          );
        })}
        {shown === 0 && <div className="subtle mono" style={{ margin: 'auto', fontSize: 12 }}>ingesting processor batch…</div>}
      </div>
      <div className="between" style={{ padding: '12px 16px', borderTop: '1px solid var(--line-soft)', background: 'var(--bg-sunken)', fontSize: 12.5 }}>
        <span className="row" style={{ gap: 6, color: 'var(--green)' }}><Icon name="circle-check" size={15} /><b className="tnum">{autoCount}</b> auto-filed</span>
        <span className="row" style={{ gap: 6, color: 'var(--amber)' }}><Icon name="arrow-up-right" size={15} /><b className="tnum">{hitlCount}</b> routed to review</span>
        <span className="subtle mono">gate: 6 checks</span>
      </div>
    </div>
  );
}

interface FlowStep {
  icon: string;
  label: string;
  sub: string;
  tone: Tone;
}

export function EngineSection() {
  useReveal();
  const flow: FlowStep[] = [
    { icon: 'scroll-text', label: 'Chargeback intake', sub: 'processor batch file', tone: 'blue' },
    { icon: 'layers', label: 'Evidence Collector', sub: 'assembles CE package', tone: 'violet' },
    { icon: 'file-signature', label: 'Representment Drafter', sub: 'writes the narrative', tone: 'violet' },
    { icon: 'gauge', label: 'Auto-submit gate', sub: '6 eligibility checks', tone: 'cyan' },
  ];
  return (
    <section className="section" id="engine" style={{ position: 'relative' }}>
      <div className="grid-bg" />
      <div className="container" style={{ position: 'relative' }}>
        <div className="row wrap" style={{ gap: 12, marginBottom: 18 }}>
          <span className="tag tag-green reveal"><Icon name="sparkles" size={12} />New · Agentic</span>
        </div>
        <SectionHead
          eyebrow="Recover · the representment engine"
          title="Agents that build the case, gates that keep you in control"
          lead="When a chargeback lands, four agents take it end-to-end — collecting evidence, drafting the representment, and clearing every eligibility gate. The clean cases file themselves. Everything ambiguous routes to a human, with the full reasoning attached."
        />

        <div className="engine-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.92fr) minmax(0, 1.08fr)', gap: 'clamp(28px, 4vw, 56px)', marginTop: 48, alignItems: 'start' }}>
          {/* left: agent flow */}
          <div className="stack reveal" style={{ gap: 14 }}>
            <div className="card" style={{ padding: 22 }}>
              <div className="mono subtle" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>How a case moves</div>
              <div className="stack" style={{ gap: 0 }}>
                {flow.map((f: FlowStep, i: number) => {
                  const c = toneVar(f.tone);
                  return (
                    <div key={i}>
                      <div className="row" style={{ gap: 14 }}>
                        <span style={{ width: 42, height: 42, borderRadius: 11, display: 'grid', placeItems: 'center', color: c, background: `color-mix(in oklch, ${c} 14%, var(--bg-1))`, border: `1px solid color-mix(in oklch, ${c} 30%, transparent)`, flexShrink: 0 }}>
                          <Icon name={f.icon} size={20} />
                        </span>
                        <div className="stack" style={{ gap: 1 }}>
                          <span style={{ fontSize: 15, fontWeight: 550 }}>{f.label}</span>
                          <span className="subtle" style={{ fontSize: 12.5 }}>{f.sub}</span>
                        </div>
                      </div>
                      {i < flow.length - 1 && <div style={{ width: 2, height: 16, background: 'var(--line)', marginLeft: 21, marginBlock: 4 }} />}
                    </div>
                  );
                })}
              </div>
              <div className="rule" style={{ margin: '18px 0' }} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="card" style={{ padding: '12px 14px', background: 'color-mix(in oklch, var(--green) 10%, var(--bg-sunken))', borderColor: 'color-mix(in oklch, var(--green) 30%, transparent)' }}>
                  <div className="row" style={{ gap: 7, color: 'var(--green)', fontSize: 13, fontWeight: 500 }}><Icon name="circle-check" size={16} />All gates pass</div>
                  <div className="subtle" style={{ fontSize: 12, marginTop: 3 }}>Filed automatically</div>
                </div>
                <div className="card" style={{ padding: '12px 14px', background: 'color-mix(in oklch, var(--amber) 10%, var(--bg-sunken))', borderColor: 'color-mix(in oklch, var(--amber) 30%, transparent)' }}>
                  <div className="row" style={{ gap: 7, color: 'var(--amber)', fontSize: 13, fontWeight: 500 }}><Icon name="arrow-up-right" size={16} />Any gate fails</div>
                  <div className="subtle" style={{ fontSize: 12, marginTop: 3 }}>Human review queue</div>
                </div>
              </div>
            </div>
            <div className="card" style={{ padding: '16px 20px' }}>
              <div className="row" style={{ gap: 10, color: 'var(--fg-soft)', fontSize: 13.5 }}>
                <Icon name="lock" size={17} style={{ color: 'var(--primary-2)' }} />
                <span className="pretty">Refund &amp; credit-issuing actions never auto-enable. Agents recommend and draft; money moves on your sign-off.</span>
              </div>
            </div>
          </div>

          {/* right: live console */}
          <div className="reveal" style={rd(1)}>
            <LiveConsole />
          </div>
        </div>

        {/* agent roster */}
        <div className="reveal" style={{ marginTop: 48 }}>
          <div className="mono subtle" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>The agents on the case</div>
          <div className="agent-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
            {agents.map((a: Agent) => {
              const c = toneVar(a.tone);
              return (
                <div key={a.id} className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div className="between">
                    <IconTile name={a.icon} tone={a.tone} size={42} icon={20} />
                    <span className="tag" style={{ height: 18, fontSize: 9.5 }}>{a.domain}</span>
                  </div>
                  <div className="row" style={{ gap: 7 }}>
                    <Icon name="bot" size={15} style={{ color: c }} />
                    <span style={{ fontSize: 15, fontWeight: 550 }}>{a.name}</span>
                  </div>
                  <p className="subtle pretty" style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>{a.blurb}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
